import { Input, InputWithIcon } from '@tempo-io/sui-input';
import { useCallback, useMemo, useRef, useState } from 'react';
import { EmptyFieldValue } from '@/components/common/item-field-value/EmptyFieldValue';
import { FieldWithStatusIndicator } from '@/components/common/item-field-value/FieldWithStatusIndicator';
import { useCommitOnEvents } from './useCommitOnEvents';

const INTL_NUMERIC_REGEX = /^[0-9.,' _-]*$/;

const parseLocaleNumber = (str: string): number | null => {
  const decimalSep = (1.1).toLocaleString().charAt(1);
  const thousandsSep = (1000).toLocaleString().charAt(1);
  const normalized = str
    .replace(new RegExp(`\\${thousandsSep}`, 'g'), '')
    .replace(new RegExp(`\\${decimalSep}`), '.');

  const parsed = Number.parseFloat(normalized);

  return !Number.isNaN(parsed) && Number.isFinite(parsed) ? parsed : null;
};

export const NumericField = ({
  name,
  id,
  value,
  isEditable = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
  prefix,
  autoFocus = false,
}: {
  id: string;
  name: string;
  value?: number;
  isEditable?: boolean;
  onChange?: (value: number) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  prefix?: string;
  autoFocus?: boolean;
}) => {
  const inputId = `numeric-field-${id}`;
  const formattedValue = useMemo(
    () => (value !== undefined ? new Intl.NumberFormat().format(value) : ''),
    [value],
  );
  const [localValue, setLocalValue] = useState(formattedValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue === '' || INTL_NUMERIC_REGEX.test(newValue)) {
      setLocalValue(newValue);
    }
  };

  const parse = useCallback((raw: string) => parseLocaleNumber(raw) ?? value, [value]);

  const onChangeNumeric = useCallback(
    (v: number | undefined) => {
      if (v !== undefined) onChange?.(v);
    },
    [onChange],
  );

  const { handleBlur, handleKeyDown } = useCommitOnEvents({
    value,
    rawValue: localValue,
    parse,
    onChange: onChangeNumeric,
    inputRef,
  });

  return (
    <FieldWithStatusIndicator
      inputId={inputId}
      name={name}
      isPending={isPending}
      isSuccess={isSuccess}
    >
      {isEditable ? (
        <div className="flex flex-col gap-1">
          {prefix ? (
            <InputWithIcon
              leftIcon={
                <span
                  className="text-sm text-text-neutral-secondary"
                  data-testid="numeric-field-prefix"
                >
                  {prefix}
                </span>
              }
              ref={inputRef}
              id={inputId}
              type="text"
              value={localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus={autoFocus}
              disabled={isPending}
              data-testid="numeric-field-input"
            />
          ) : (
            <Input
              ref={inputRef}
              id={inputId}
              type="text"
              value={localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus={autoFocus}
              disabled={isPending}
              data-testid="numeric-field-input"
            />
          )}
          {isError && error && (
            <span className="text-xs text-text-error" data-testid="numeric-field-error">
              {error.message}
            </span>
          )}
        </div>
      ) : (
        <div className="text-sm text-text-neutral-primary" data-testid="numeric-field-value">
          {formattedValue || <EmptyFieldValue />}
        </div>
      )}
    </FieldWithStatusIndicator>
  );
};
